import React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import WarningIcon from '@material-ui/icons/Warning';
import Skeleton from '@material-ui/lab/Skeleton';
import Header from '../../components/utils/Header';
import Version from '../../api/Version';
import MoreMenu from '../../components/utils/MoreMenu';
import DeleteDialog from '../../components/DeleteDialog';
import { chainAsyncCalls } from '../../utils';
import { cycle } from '../../store/Cycle';
import { statOrange } from '../../globals';
import LoadingButton from '../../components/utils/LoadingButton';
import DownloadButton from '../../components/utils/DownloadButton';
import PermissionChecker from '../../components/permissions/PermissionChecker';
import ForbiddenView from '../../views/ForbiddenView';


const styles = theme => ({
  disabled: {
    color: theme.palette.text.disabled,
  },
  warning: {
    color: statOrange,
  }
});


@withStyles(styles)
@observer
class VersionListView extends React.Component {
  @observable updating = true;
  @observable versions = [];
  @observable importStockLoading = false;
  @observable importProductionPlanLoading = false;
  @observable importOthersLoading = false;

  async componentDidMount() {
    await this.updateData();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.location !== prevProps.location) {
      this.updateData();
    }
  }

  @action updateData = chainAsyncCalls(async () => {
    this.updating = true;
    this.versions = await Version.getList(cycle.params);
    let response = await Version.ajaxRequest('get', '/import_stock/');
    this.importStockLoading = response.data.running;
    response = await Version.ajaxRequest('get', '/import_production_plan/');
    this.importProductionPlanLoading = response.data.running;
    response = await Version.ajaxRequest('get', '/import_others/');
    this.importOthersLoading = response.data.running;
    this.updating = false;
  });

  @action onImportStock = chainAsyncCalls(async () => {
    this.importStockLoading = true;
    await Version.ajaxRequest(
      'post',
      '/import_stock/',
      { params: cycle.params },
    );
  });

  @action onImportProductionPlan = chainAsyncCalls(async () => {
    this.importProductionPlanLoading = true;
    await Version.ajaxRequest(
      'post',
      '/import_production_plan/',
      { params: cycle.params },
    );
  });

  @action onImportOthers = chainAsyncCalls(async () => {
    this.importOthersLoading = true;
    await Version.ajaxRequest(
      'post',
      '/import_others/',
      { params: cycle.params },
    );
  });

  get table() {
    if ((Version.fieldsData === null) || this.updating) {
      return <Skeleton variant="rect" height={550} />
    }
    const { classes } = this.props;
    console.log(this.versions, 1100000)
    return (
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cycle</TableCell>
              <TableCell>{Version.fieldsData.technology_family.label}</TableCell>
              <TableCell>{Version.fieldsData.type.label}</TableCell>
              <TableCell>{Version.fieldsData.name.label}</TableCell>
              <TableCell align="center">{Version.fieldsData.certified.label}</TableCell>
              <TableCell align="center">{Version.fieldsData.file.label}</TableCell>
              <TableCell align="center">Warnings</TableCell>
              <TableCell align="right">{Version.fieldsData.created_by.label}</TableCell>
              <TableCell align="right">{Version.fieldsData.created_at.label}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {
              this.versions.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={8} className={classes.disabled}>
                      <Typography align="center">
                        No input files for this cycle.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
                : this.versions.map(version =>
                  <TableRow key={version.id}>
                    <TableCell>
                      <code>{version.cycleStr}</code>
                    </TableCell>
                    <TableCell>{version.verboseTechnologyFamily}</TableCell>
                    <TableCell>
                      <small>{version.verboseType}</small>
                    </TableCell>
                    <TableCell>{version.name}</TableCell>
                    <TableCell align="center">
                      <CheckIcon opacity={version.certified ? 1 : 0} />
                    </TableCell>
                    <TableCell size="small" align="center">
                      <DownloadButton
                        href={version.file}
                        permission={version.canBeDownloaded}
                        isAPOProdPlan={version.type === 'production_plan' || version.type === 'standard_cost_prices'}
                        versionId={version.id}
                      />
                    </TableCell>
                    <TableCell size="small" align="center">
                      {
                        version.warnings_file === null
                          ? null
                          : (
                            <IconButton component="a" href={version.warnings_file} className={classes.warning}>
                              <WarningIcon />
                            </IconButton>
                          )
                      }
                    </TableCell>
                    <TableCell align="right">
                      {
                        version.createdByUser === null
                          ? <em>Automatic import</em>
                          : version.createdByUser.toString()
                      }
                    </TableCell>
                    <TableCell align="right">
                      {new Date(version.created_at).toLocaleString('en-US')}
                    </TableCell>
                    <TableCell size="small" align="right">
                      <MoreMenu>
                        <PermissionChecker
                          permission={version.canBeEdited}
                          placement="left"
                        >
                          {
                            hasPermission =>
                              <MenuItem
                                component={Link}
                                to={version.editUrl}
                                disabled={!hasPermission}
                              >
                                <ListItemIcon><EditIcon /></ListItemIcon>
                                Edit
                              </MenuItem>
                          }
                        </PermissionChecker>
                        <PermissionChecker
                          permission={version.canBeDeleted}
                          placement="left"
                        >
                          {
                            hasPermission =>
                              <DeleteDialog
                                instance={version}
                                triggerFunc={handleOpen =>
                                  <MenuItem
                                    onClick={handleOpen}
                                    disabled={!hasPermission}
                                  >
                                    <ListItemIcon><DeleteIcon /></ListItemIcon>
                                    Delete
                                  </MenuItem>
                                }
                                onDelete={this.updateData}
                              />
                          }
                        </PermissionChecker>
                      </MoreMenu>
                    </TableCell>
                  </TableRow>
                )
            }
          </TableBody>
        </Table>
      </Paper>
    );
  }

  render() {
    if (Version.fieldsData === null) {
      return <ForbiddenView />
    }
    return (
      <Grid container direction="column" spacing={2}>
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Input Files" />
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <PermissionChecker permission="athena.import_stock">
                  {
                    hasPermission =>
                      <LoadingButton
                        variant="outlined"
                        onClick={this.onImportStock}
                        loading={this.importStockLoading}
                        disabled={!hasPermission}
                      >
                        Import stock
                      </LoadingButton>
                  }
                </PermissionChecker>
              </Grid>
              <Grid item>
                <PermissionChecker permission="athena.import_production_plan">
                  {
                    hasPermission =>
                      <LoadingButton
                        variant="outlined"
                        onClick={this.onImportProductionPlan}
                        loading={this.importProductionPlanLoading}
                        disabled={!hasPermission}
                      >
                        Import production plan
                      </LoadingButton>
                  }
                </PermissionChecker>
              </Grid>
              <Grid item>
                <PermissionChecker permission="athena.import_others">
                  {
                    hasPermission =>
                      <LoadingButton
                        variant="outlined"
                        onClick={this.onImportOthers}
                        loading={this.importOthersLoading}
                        disabled={!hasPermission}
                      >
                        Import others
                      </LoadingButton>
                  }
                </PermissionChecker>
              </Grid>
              <Grid item>
                <PermissionChecker permission="athena.add_version">
                  {
                    hasPermission =>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon viewBox="2 2 20 20"/>}
                        component={Link}
                        to="/input-files/create"
                        disabled={!hasPermission}
                      >
                        Create
                      </Button>
                  }
                </PermissionChecker>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {this.table}
        </Grid>
      </Grid>
    );
  }
}


export default VersionListView;
